// Shared viewer context. Composite of domain-specific buckets defined under
// ./context/*; this file just wires them together. Modules read freely;
// writes are localised to whichever bucket owns the field.
//
// Created once at startup in src/app/main.ts. The reactive store
// (src/store/instance.ts) holds *params* and *selection*; this viewer holds
// everything else.

import { createThreeRefs, type ThreeRefs, type SceneCamera } from './context/three';
import { createGridState, type GridState, type AxisRange, type AxisExtents } from './context/grid';
import {
    createSceneMeta,
    type SceneMeta,
    type FieldlineConfig,
    type SelectionMeta,
    type AxisVisualConfig,
    type UiDriver,
    type UiMeta,
    type LightingChannel,
    type LightingConfig,
    type DatasetEntry,
} from './context/sceneMeta';
import { createFieldState, type FieldState } from './context/field';
import { createColormapState, type ColormapState } from './context/colormap';
import { createThemeState, type ThemeState } from './context/theme';
import {
    createObjectsState,
    type ObjectsState,
    type SelectionObjects,
    type SelectionsObjects,
    type AxesObjects,
    type SliceState,
    type SlicesObjects,
    type VolumeObjects,
    type FieldlinesObjects,
    type ContoursObjects,
    type RotatingObject,
    type SliceAxisKey,
} from './context/objects';
import {
    createInputState,
    createLoopState,
    createDisposeState,
    type InputState,
    type LoopState,
    type LoopHandle,
    type DisposeState,
} from './context/runtime';

// Re-export every sub-type so existing `import type { ... } from '@app/context'`
// callsites keep working unchanged.
export type {
    ThreeRefs, SceneCamera,
    GridState, AxisRange, AxisExtents,
    SceneMeta, FieldlineConfig, SelectionMeta, AxisVisualConfig,
    UiDriver, UiMeta, LightingChannel, LightingConfig, DatasetEntry,
    FieldState,
    ColormapState,
    ThemeState,
    ObjectsState, SelectionObjects, SelectionsObjects, AxesObjects, SliceState, SlicesObjects,
    VolumeObjects, FieldlinesObjects, ContoursObjects, RotatingObject, SliceAxisKey,
    InputState, LoopState, LoopHandle, DisposeState,
};

export interface ViewerContext {
    three: ThreeRefs;
    grid: GridState;
    sceneMeta: SceneMeta;
    field: FieldState;
    colormap: ColormapState;
    theme: ThemeState;
    objects: ObjectsState;
    input: InputState;
    loop: LoopState;
    dispose: DisposeState;
}

export function createViewerContext(): ViewerContext {
    return {
        three: createThreeRefs(),
        grid: createGridState(),
        sceneMeta: createSceneMeta(),
        field: createFieldState(),
        colormap: createColormapState(),
        theme: createThemeState(),
        objects: createObjectsState(),
        input: createInputState(),
        loop: createLoopState(),
        dispose: createDisposeState(),
    };
}
